import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import MappingProps, { MappingValidationResult } from 'types/Mapping';
import pluralize from 'pluralize';
import _compact from 'lodash/compact';
import _flatten from 'lodash/flatten';
import _uniq from 'lodash/uniq';
import _partition from 'lodash/partition';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import ValidationStatusIndicator from 'atoms/ValidationStatusIndicator';

interface Props {
  mappings: MappingProps[];
  errorsNotTiedToSpecificMappingRows?: MappingValidationResult[];
}

function OverallValidationStatus({ mappings, errorsNotTiedToSpecificMappingRows = [] }: Props) {
  const classes = useStyles();
  const mappingValidationChecks = _flatten(mappings.map(m => m.validation));
  const validationChecks = _uniq(
    _compact([...mappingValidationChecks, ...errorsNotTiedToSpecificMappingRows]),
  );

  if (!validationChecks.length) {
    return (
      <div className={classes.root}>
        <ValidationStatusIndicator status="success" />
      </div>
    );
  }

  const [errors, warnings] = _partition(validationChecks, vc => vc.type === 'error');

  // (note - pearl) Show warnings ONLY WHEN there are no errors
  if (!errors.length) {
    const renderWarningCount = () => (
      <Typography variant="body2" className={classes.text}>
        {warnings.length} {pluralize('warning', warnings.length)}
      </Typography>
    );

    if (warnings.length === 1) {
      return (
        <Tooltip
          arrow
          title={<div dangerouslySetInnerHTML={{ __html: warnings[0].longDescriptionHtml }} />}
        >
          <div className={classes.root}>
            <ValidationStatusIndicator status={'warning'} />
            {renderWarningCount()}
          </div>
        </Tooltip>
      );
    }

    return (
      <div className={classes.root}>
        <ValidationStatusIndicator status={'warning'} />
        {renderWarningCount()}
      </div>
    );
  }

  const renderErrorCount = () => (
    <Typography variant="body2" className={classes.text}>
      {errors.length} {pluralize('error', errors.length)}
    </Typography>
  );

  if (errors.length === 1) {
    return (
      <Tooltip
        arrow
        title={<div dangerouslySetInnerHTML={{ __html: errors[0].longDescriptionHtml }} />}
      >
        <div className={classes.root}>
          <ValidationStatusIndicator status={'error'} />
          {renderErrorCount()}
        </div>
      </Tooltip>
    );
  }

  return (
    <div className={classes.root}>
      <ValidationStatusIndicator status={'error'} />
      {renderErrorCount()}
    </div>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  text: {
    marginLeft: theme.spacing(),
    color: theme.colors.gray1,
  },
}));

export default OverallValidationStatus;
